import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  InputNumber,
  Button,
  Row,
  Col,
  message,
  Modal,
  Select,
  DatePicker,
  TimePicker,
  notification,
  Space,
} from "antd";
import moment from "moment";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { updateBookingAirport } from "../../../api/booking.service";
import { getTourPackages } from "../../../api/tour-package.service";
import { getVehiclesTypes } from "../../../api/vehicles-type.service";

const { Option } = Select;
const { TextArea } = Input;

const EditAirportBookingPackageModal = ({
  vehicleData,
  onClose,
  fetchVehicles,
  isModalOpen,
  setIsModalOpen,
  handleOk,
  handleCancel,
  isViewOnly = false,
}) => {
  const [form] = Form.useForm();
  const [tourPackages, setTourPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Fetch available vehicles (using getVehiclesTypes as source)
  const fetchPackages = async () => {
    try {
      setLoading(true);
      const data = await getVehiclesTypes();
      setTourPackages(data?.data || []);
    } catch (err) {
      notification.error({ message: "Failed to fetch vehicles" });
      setError("Failed to fetch tours");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    if (vehicleData) {
      const dateTime = vehicleData.dateTime
        ? moment.utc(vehicleData.dateTime).tz("Asia/Karachi")
        : null;

      form.setFieldsValue({
        ...vehicleData,
        name: vehicleData.name,
        email: vehicleData.email,
        phone: vehicleData.phone,
        type: vehicleData.type,
        date: dateTime ? dateTime.clone().startOf("day") : null,
        time: dateTime ? dateTime.clone() : null,
        pickupType: vehicleData.pickupType,
        pickup: vehicleData.pickup,
        destinationType: vehicleData.destinationType,
        destination: vehicleData.destination,
        passengerCount: vehicleData.passengerCount,
        luggageCount: vehicleData.luggageCount,
        flightNumber: vehicleData.flightNumber,
        totalDistance: vehicleData.totalDistance,
        totalDuration: vehicleData.totalDuration,
        selectedVehicle: vehicleData.selectedVehicle?.id,
        driversInstructions: vehicleData.driversInstructions,
        status: vehicleData.status,
        additionalPickups:
          vehicleData.additionalPickups
            ?.filter((elem) => Object.keys(elem).length > 0)
            .map((elem) => elem.pickup) || [],
      });
    }
  }, [vehicleData, form]);

  const onFinish = async (values) => {
    try {
      // Merge date and time (already in PKT)
      const mergedDateTime = values.date
        .clone()
        .set("hour", values.time.hour())
        .set("minute", values.time.minute())
        .set("second", 0)
        .set("millisecond", 0);

      // Convert to UTC for backend
      const dateTimeInUTC = mergedDateTime.clone().utc();

      const payload = {
        summaryObj: vehicleData.summaryObj,
        ...values,
        dateTime: dateTimeInUTC.toISOString(),
        additionalPickups: values.additionalPickups.map((elem) => ({
          pickup: elem,
          type: "address",
        })),
      };

      // Remove date and time fields (since merged into dateTime)
      delete payload.date;
      delete payload.time;

      await updateBookingAirport(vehicleData.referenceNo, payload);

      message.success("Booking updated successfully!");
      fetchVehicles();
      setIsModalOpen(false);
    } catch (error) {
      message.error(error.message || "Something went wrong.");
      console.error("Error updating booking:", error);
    }
  };

  return (
    <Modal
      className="vt-modal"
      footer={null}
      destroyOnClose
      title={`${isViewOnly ? "View" : "Edit"} Airport Booking`}
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      onClose={handleCancel}
      width={1000}
      style={{
        maxHeight: "90vh"
      }}
      bodyStyle={{
        overflowY: "auto",
        maxHeight: "calc(90vh - 100px)", // Account for header/footer
        scrollbarWidth: "thick", // For Firefox
        "&::-webkit-scrollbar": {
          width: "12px" // For Chrome/Safari/Edge
        }
      }}
    >
      <Form disabled={isViewOnly} layout="vertical" form={form} onFinish={onFinish}>
        {/* Row 1 */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter the customer's name" }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter the customer's email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Phone" name="phone" rules={[{ required: true, message: "Please enter the customer's phone" }]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 2 */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Type" name="type" rules={[{ required: true, message: "Please enter booking type" }]}>
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Date" name="date" rules={[{ required: true, message: "Please select a date" }]}>
              <DatePicker style={{ width: "100%" }} format="DD MMMM YYYY" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Time" name="time" rules={[{ required: true, message: "Please select time" }]}>
              <TimePicker style={{ width: "100%" }} format="HH:mm" />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 3 */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Pickup Type"
              name="pickupType"
              rules={[{ required: true, message: "Please select pickup type" }]}
            >
              <Select placeholder="Select pickup type">
                <Option value="airport">Airport</Option>
                <Option value="address">Address</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Pickup" name="pickup" rules={[{ required: true, message: "Please enter pickup location" }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Destination Type"
              name="destinationType"
              rules={[{ required: true, message: "Please select destination type" }]}
            >
              <Select placeholder="Select destination type">
                <Option value="airport">Airport</Option>
                <Option value="address">Address</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Row 4 */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Destination" name="destination" rules={[{ required: true, message: "Please enter destination" }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Passenger Count" name="passengerCount" rules={[{ required: true, message: "Value must be between 1 and 30 inclusive"}]}>
              <InputNumber min={1} max={30} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Luggage" name="luggageCount">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 5 */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Flight Number" name="flightNumber">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Total Distance" name="totalDistance">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Total Duration" name="totalDuration">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 6 */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Selected Vehicle"
              name="selectedVehicle"
              rules={[{ required: true, message: "Please select a vehicle" }]}
            >
              <Select
                placeholder="Select a vehicle"
                options={
                  tourPackages?.map((pkg) => ({
                    label: pkg.name,
                    value: pkg.id,
                  })) || []
                }
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Driver's Instructions" name="driversInstructions">
              <TextArea rows={1} placeholder="Instructions" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Status" name="status" rules={[{ required: true, message: "Please select a status" }]}>
              <Select>
                <Option value="READY">READY</Option>
                <Option value="ENROUTE">ENROUTE</Option>
                <Option value="ONBOARD">ONBOARD</Option>
                <Option value="NOSHOW">NOSHOW</Option>
                <Option value="CANCELLED">CANCELLED</Option>
                <Option value="COMPLETED">COMPLETED</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Additional Pickups Row (Full Width) */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.List name="additionalPickups">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} align="baseline" style={{ display: "flex", marginBottom: 8 }}>
                      {!isViewOnly && (
                        <MinusCircleOutlined onClick={() => remove(name)} />
                      )}
                      <Form.Item
                        {...restField}
                        name={name}
                        rules={[{ required: true, message: "Please enter an additional pickup" }]}
                        style={{ width: "150%", marginRight: '5rem' }}
                      >
                        <Input placeholder="Additional Pickup" />
                      </Form.Item>
                    </Space>
                  ))}
                  {!isViewOnly && (
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        Add Additional Pickup
                      </Button>
                    </Form.Item>
                  )}
                </>
              )}
            </Form.List>
          </Col>
        </Row>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            Update
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditAirportBookingPackageModal;
